// Your accounts (paczka 12, artboards 01–04). Keep keys in sync with pl/appAccounts.ts.
//
// NO PROVIDER NAME AND NO INTERMEDIARY IN CUSTOMER-FACING COPY (`locales-no-intermediary`).
// No login-protocol name either: “official login”; the platform is a word NEXT TO the
// account, never in a heading.
export const appAccounts = {
  // The screen title is "Publishing" (the five-tab frame, session 35) and the tab is
  // called "Accounts". This key stays as the view's name for screen readers and for the
  // older tests; the intro sentence left the screen (rule 8) - the platform dialog's one
  // sentence already says it.
  title: 'Your accounts',
  // Rule 24: the state of the WHOLE feature as a pill in the title row, lower case.
  feature: {
    paused: 'publishing paused',
  },
  // The pill of a row whose daily cap for that account is already used up.
  limitPill: 'daily cap reached',

  platform: {
    tiktok: 'TikTok',
    instagram: 'Instagram',
    youtube: 'YouTube',
  },
  // Polish declines the platform name after „na” (na TikToku / Instagramie / YouTube),
  // so sentences that say „stays on …” take this form instead of `platform.*`
  // (MAPA 23, decision 13). English has no such case: the label IS the form, and the
  // key exists here so both languages interpolate the same variable.
  platformLoc: {
    tiktok: 'TikTok',
    instagram: 'Instagram',
    youtube: 'YouTube',
  },
  // English has no case: the genitive is the label itself.
  platformGen: {
    tiktok: 'TikTok',
    instagram: 'Instagram',
    youtube: 'YouTube',
  },
  // RULE 32 (Domi, 20.09): a platform card is a glyph + a name + a "Connect" action. No
  // descriptions of what each platform can do, no length limits, no formats; the ONLY text
  // under a name belongs to Instagram, because there only a particular kind of account
  // really works. Keys that died with that rule: `tiktok.req`, `tiktok.fmt`,
  // `instagram.fmt`, `youtube.req`, `youtube.fmt`, `password`, `next`.
  platformDialog: {
    title: 'Which platform are you connecting?',
    // The dialog's only sentence: a legal requirement, not a description of features.
    official: 'Connect an account through the official login. We publish only what you approve.',
    instagram: {
      req: 'Business or creator account.',
    },
    connect: 'Connect',
    cancel: 'Cancel',
  },
  stats: {
    connectedLabel: 'Your accounts',
  },
  status: {
    connected: 'active',
    disconnected: 'disconnected',
    expired: 'sign in again',
    suspended: 'beyond your plan',
  },
  action: {
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
  },
  slots: {
    label: 'Slots in your plan',
    buy: 'Add a slot',
    full: 'All slots are assigned for this period',
    // The pill of a row whose slot is still held by an identity that left the list. The
    // sentence about how long a slot stays assigned left the screen (rule 32) and lives on
    // in the Terms and in the FAQ (`faq.ts`, the question about slots) - that is its place.
    heldPill: 'slot held',
    settlementPill: 'manual settlement · {n}',
  },
  disconnect: {
    title: 'Disconnect {handle}?',
    body: 'EDIMI loses access to publishing on this account. Published posts stay on {platformLoc}.',
    // Two pre-redesign dialog labels. They left the Accounts screen (one sentence with a
    // number, 09b), but the preview bench `routes/a25/p06States.tsx` still asks for them.
    willCancel: 'Scheduled posts will be cancelled',
    willRefund: 'Tokens back in your pool',
    // The dialog's ONE sentence, with the number from the backend (09b).
    willCancelCount: '{n} scheduled posts will be cancelled.',
    willCancelCount_one: '{n} scheduled post will be cancelled.',
    willCancelCount_few: '{n} scheduled posts will be cancelled.',
    willCancelCount_many: '{n} scheduled posts will be cancelled.',
    keep: 'Keep it',
    toast: 'Disconnected {handle} · {n} tokens returned',
    toast_one: 'Disconnected {handle} · {n} token returned',
    toast_few: 'Disconnected {handle} · {n} tokens returned',
    toast_many: 'Disconnected {handle} · {n} tokens returned',
  },
  // The sentence about a lost authorisation left through rule 8: the "sign in again" pill
  // next to the account says it, and "Reconnect" beside it is the way out. The same
  // happened to the `settlementNote_*` notes (a toast carries the fact with its number of
  // tokens) and to the "publishing is temporarily down" dialog - the state of the WHOLE
  // feature is a pill in the title row today (rule 24).
  error: {
    identityMismatch: 'A different account was selected for this slot. Further attempts are paused until the period ends. Contact us if this was a mistake.',
    connectionPending: 'The previous sign-in is still pending. Complete it or try again in 15 minutes.',
    // The way back from the Planner's error dialog (`routes/Campaigns.tsx`, package 35a).
    back: 'Back to your accounts',
    // Point 41 of session 35: "fetch" means downloading a file in this product.
    title: 'The accounts could not be loaded.',
    retry: 'Try again',
    // Coming back from the Google window with no channel (artboard 02): not an outage,
    // just a Google account that has none. A bar under the title, not a toast — the
    // artist comes back from an external window and has to read it without hurrying.
    youtubeNoChannel: 'This Google account has no channel. Start over and pick an account with a channel in the Google window.',
    // Every other failed return: the vendor named no cause, so neither do we.
    connectFailed: 'Could not connect the account. Try again.',
  },
  // On the Accounts screen "publishing is temporarily down" is a pill (rule 24), but the
  // Planner (`routes/Campaigns.tsx`, package 35a) still carries the same sentence.
  unavailable: {
    title: 'Publishing is temporarily down',
    body: 'Your campaigns are safe and scheduled posts are waiting. We resume them automatically.',
  },
  toolbar: {
    connect: 'Connect an account',
    // The busy state of the primary action while the platform's login window opens (09h).
    connecting: 'Opening the login…',
  },
  // The empty state (09e): glyph, title, ONE action. No second sentence (rule 8); the slot
  // meter stays above it, because it is what says how many accounts can be connected at all.
  empty: {
    title: 'No connected accounts',
  },
  table: {
    thToday: 'Today',
    // The daily cap as a row COLUMN (artboard 08a): "today 2/5", not a sentence about caps.
    // Its own key, because `appCampaigns.detail.limitToday` belongs to the Planner.
    todayOf: 'today {n}/{max}',
    // The name of the refresh action: on screen it is a bare 44 px glyph (rule 32), so this
    // text exists for screen readers only.
    refresh: 'Refresh list',
  },
  // Round 45-B, package PK: the „Advanced: @account” sheet (erratum A.1: publishing settings ONCE per
  // account, Terms §10.p5). Two blocks with no sentence headings: Publishing and Limits (rule 8).
  advanced: {
    title: 'Advanced: {handle}',
    // `warn` pill on the account row (Accounts) and in the Planner's Accounts step (rules 5 / 24).
    noSettings: 'no publishing settings',
    visibility: 'Visibility',
    comment: 'Comments',
    duet: 'Duet',
    stitch: 'Stitch',
    brand: 'Brand',
    brandOrganic: 'Your brand',
    brandContent: 'Paid partnership',
    kids: 'Made for kids',
    yes: 'Yes',
    no: 'No',
    yt: {
      public: 'Public',
      unlisted: 'Unlisted',
      private: 'Private',
    },
    maxPerDay: 'Posts per day',
    hours: 'Hours',
    // Screen readers: the two hour steppers are named „from” / „to”.
    from: 'From',
    to: 'To',
    gap: 'Gap',
    gapValue: '{n} h',
    ownSchedule: 'Own schedule',
    week: 'Week',
    less: 'Less: {label}',
    more: 'More: {label}',
    save: 'Save',
    saved: 'Saved settings for {handle}.',
    saveFailed: "Couldn't save the settings.",
    loadFailed: "Couldn't load the settings.",
    retry: 'Retry',
    // TikTok returned no visibility options and there is no copy: a pill in the visibility row (PK-8);
    // the full reason for screen readers only.
    noOptions: 'no options',
    noOptionsReason: "Couldn't load the visibility options from TikTok.",
    back: 'Back to the list',
  },
}
